import React, { useState, useRef, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaFileAlt, FaEnvelope, FaChevronDown, FaChevronUp, FaComments, FaEye, FaTrash } from "react-icons/fa";
import { FiUploadCloud } from "react-icons/fi";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import Modal from "../Popups/Modal";
import { submitSupportTicket } from "../api/support.api";
import { getStaffId } from "../utils/storage";
import { toast } from "../Components/Toast";

const FAQ_ITEMS = [
  {
    q: "How do I read my recent lab test results?",
    a: "Your lab results are categorized into standard ranges in the Health Records section. Navigate to Medical Records from your dashboard to view detailed breakdowns, reference ranges, and historical trends for each test.",
  },
  {
    q: "What wellness benefits are included in my plan?",
    a: "Your corporate wellness plan typically includes consultations, lab tests, health risk assessments, and wallet credits for approved services. Check your Wallet page for available balance and utilization details.",
  },
  {
    q: "Can I change my primary care physician (PCP)?",
    a: "Yes. Book a new consultation with your preferred doctor from the Consultations section. Your care team will be updated based on your active bookings and medical history.",
  },
];

const HelpPage = () => {
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(0);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [reason, setReason] = useState("Payment Issues");
  const [description, setDescription] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ticketSuccess, setTicketSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const previewUrl = useMemo(
    () => (attachedFile ? URL.createObjectURL(attachedFile) : null),
    [attachedFile]
  );

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const clearAttachment = () => {
    setAttachedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const filteredFaq = useMemo(() => {
    if (!search.trim()) return FAQ_ITEMS;
    const q = search.toLowerCase();
    return FAQ_ITEMS.filter(
      (item) => item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)
    );
  }, [search]);

  const handleSubmitTicket = async () => {
    const staffId = getStaffId();
    if (!staffId) {
      toast.error("You are not logged in!");
      return;
    }
    if (!description.trim()) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      if (attachedFile) formData.append("file", attachedFile);
      formData.append("reason", reason);
      formData.append("description", description);

      await submitSupportTicket(formData);
      toast.success("Your support ticket was submitted successfully.");
      setTicketSuccess(true);
      setDescription("");
      clearAttachment();
      setReason("Payment Issues");
      setTimeout(() => {
        setTicketSuccess(false);
        setTicketOpen(false);
      }, 2500);
    } catch {
      toast.error("There was an error while submitting your ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <div className="pb-16 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="mb-8 text-center mx-auto max-w-2xl">
            <h1 className="text-2xl sm:text-3xl font-bold text-on-primary-fixed-var">Support &amp; Chat Desk</h1>
            <p className="text-on-surface-variant mt-1">
              Get help with your wellness plan and benefits, or message your doctors on the dedicated chat page.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant" size={16} />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search for answers (e.g. 'lab results', 'therapy coverage')…"
                  className="w-full pl-11 pr-4 py-3 border border-surface-container rounded-xl bg-surface-lowest text-sm"
                />
              </div>

              <div className="bg-surface-lowest border border-surface-container rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-4 border-b border-surface-container">
                  <FaFileAlt className="text-primary" />
                  <h2 className="font-semibold text-on-primary-fixed-var">Frequently Asked Questions</h2>
                </div>
                {filteredFaq.length === 0 ? (
                  <p className="p-5 text-sm text-on-surface-variant">No matching FAQs.</p>
                ) : (
                  filteredFaq.map((item, i) => {
                    const idx = FAQ_ITEMS.indexOf(item);
                    const isOpen = openFaq === idx;
                    return (
                      <div key={item.q} className="border-b border-surface-container last:border-0">
                        <button
                          type="button"
                          onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-surface-low/50"
                        >
                          <span className="font-medium text-on-primary-fixed-var text-sm pr-4">{item.q}</span>
                          {isOpen ? <FaChevronUp className="shrink-0 text-on-surface-variant" /> : <FaChevronDown className="shrink-0 text-on-surface-variant" />}
                        </button>
                        {isOpen && (
                          <p className="px-5 pb-4 text-sm text-on-surface-variant leading-relaxed">{item.a}</p>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="bg-surface-lowest border border-surface-container rounded-xl p-5 flex flex-col gap-4">
                <div>
                  <p className="font-semibold text-on-primary-fixed-var">Still need help?</p>
                  <p className="text-sm text-on-surface-variant">Submit a ticket or chat with doctors you&apos;ve consulted.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Link
                    to="/chat"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white font-semibold rounded-lg hover:opacity-90"
                  >
                    <FaComments size={14} /> Chat with Doctors
                  </Link>
                  <button
                    type="button"
                    onClick={() => setTicketOpen(true)}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border-2 border-primary text-primary font-semibold rounded-lg hover:bg-primary-fixed/20"
                  >
                    <FaEnvelope size={14} /> Submit Ticket
                  </button>
                </div>
              </div>
          </div>
        </div>
        <Footer />
      </div>

      <Modal open={ticketOpen} onClose={() => !submitting && setTicketOpen(false)} className="relative p-6 sm:p-8 max-w-lg">
        <h2 className="text-xl font-semibold text-on-primary-fixed-var mb-4">Submit Support Ticket</h2>
        {ticketSuccess ? (
          <p className="text-primary font-medium py-4">Ticket received! Our team will contact you soon.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Reason</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full p-3 border border-surface-container rounded-lg bg-surface-lowest"
              >
                {["Payment Issues", "Booking Issues", "Technical Issues", "Account Issue", "Consultation Issue", "App Bug", "Other"].map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your issue in detail…"
                rows={4}
                className="w-full p-3 border border-surface-container rounded-lg resize-y"
              />
            </div>
            <div
              className="border border-dashed border-surface-container rounded-lg p-6 text-center cursor-pointer hover:bg-surface-low"
              onClick={() => fileInputRef.current?.click()}
            >
              <FiUploadCloud className="mx-auto mb-2 text-on-surface-variant" size={24} />
              <p className="text-sm text-on-surface-variant">
                {attachedFile ? attachedFile.name : "Tap to attach a file (optional)"}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setAttachedFile(f);
                }}
              />
            </div>
            {attachedFile && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-primary-fixed/20 border border-primary-fixed-dim rounded-xl p-3">
                  <FaFileAlt className="text-primary shrink-0" size={20} />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm break-words">{attachedFile.name}</p>
                    <p className="text-xs text-on-surface-variant">
                      {(attachedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  {(attachedFile.type === "application/pdf" || attachedFile.type.startsWith("image/")) && (
                    <button
                      type="button"
                      onClick={() => previewUrl && window.open(previewUrl, "_blank", "noopener")}
                      className="text-primary p-2 rounded-full hover:bg-primary-fixed/30"
                      aria-label="Preview file"
                    >
                      <FaEye size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={clearAttachment}
                    className="text-error p-2 rounded-full hover:bg-error-container/30"
                    aria-label="Remove file"
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
                {attachedFile.type.startsWith("image/") && previewUrl && (
                  <img
                    src={previewUrl}
                    alt="Attachment preview"
                    className="max-h-32 rounded-lg border border-surface-container object-contain mx-auto"
                  />
                )}
              </div>
            )}
            <button
              type="button"
              onClick={handleSubmitTicket}
              disabled={submitting || !description.trim()}
              className="w-full py-3 bg-primary text-white font-semibold rounded-lg disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit Ticket"}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default HelpPage;
